import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EyeIcon,
  FolderIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import toast from "../../utils/toast";
import { ActivityTableConfigurator } from "../../components/reports/ActivityTableConfigurator";
import { apiService } from "../../services/apiService";
import { exportRowsToExcel } from "../../utils/excel";
import {
  ActivityTableColumnPreference,
  dedupeActivityTableColumns,
  normalizeActivityTableLabel,
} from "../../utils/activityTableColumns";
import { OverflowInfoCell } from "./OverflowInfoCell";
import { SearchableFilterSelect } from "./SearchableFilterSelect";
import { SortIcon } from "./SortIcon";
import { useVirtualizedRows } from "./hooks/useVirtualizedRows";
import {
  PROJECT_NUMERIC_FILTER_KEY_BY_COLUMN_ID,
  PROJECT_SORT_FIELD_BY_COLUMN_ID,
  PROJECT_TABLE_SCREEN_KEY,
  collectProjectFormColumns,
  createProjectFormColumnPreference,
  formatProjectDate,
  getProjectContainerFormElements,
  getProjectFormColumnDateValue,
  getProjectFormColumnFilterOptions,
  getProjectFormColumnNumericValue,
  getProjectFormColumnValue,
  getProjectFormColumnValueType,
  isNumericProjectColumn,
  normalizeProjectFormColumnSemanticLabel,
  parseProjectFormData,
  parseProjectFormNumericValue,
  ProjectTableColumnPreference,
} from "./projectTableUtils";

const DEFAULT_COLUMN_MIN_WIDTHS: Record<string, string> = {
  projectName: "min-w-[400px]",
  projectCode: "min-w-[130px]",
  createdAt: "min-w-[140px]",
  workflowName: "min-w-[220px]",
  clientName: "min-w-[220px]",
  managerName: "min-w-[220px]",
  startDate: "min-w-[140px]",
  endDate: "min-w-[140px]",
  spentHours: "min-w-[120px]",
  plannedHours: "min-w-[120px]",
  remainingHours: "min-w-[140px]",
  projectAmount: "min-w-[150px]",
  vatAmount: "min-w-[120px]",
  invoiceTotal: "min-w-[170px]",
  totalCollected: "min-w-[140px]",
  remainingCollection: "min-w-[150px]",
  realizedCost: "min-w-[140px]",
  remainingEstimatedCost: "min-w-[170px]",
  totalEstimatedCost: "min-w-[170px]",
  netProfit: "min-w-[150px]",
  estimatedNetProfit: "min-w-[170px]",
};

type MetricAccumulator = {
  sum: number;
  hasValue: boolean;
  missingConversion: boolean;
};

const createMetricAccumulator = (): MetricAccumulator => ({
  sum: 0,
  hasValue: false,
  missingConversion: false,
});

const DEFAULT_PROJECT_FORM_COLUMN_LABELS = [
  "Mimari Ofis",
  "İnşaat m^2",
] as const;
const PROJECT_TABLE_DEFAULT_COLUMNS_VERSION = 2;

export const ProjectsTab: React.FC<any> = (props) => {
  const {
    projects,
    filteredProjects,
    projectSearchTerm,
    setProjectSearchTerm,
    projectWorkflowFilter,
    setProjectWorkflowFilter,
    projectArchiveFilter,
    setProjectArchiveFilter,
    workflowText,
    uniqueProjectWorkflows,
    projectClientFilter,
    setProjectClientFilter,
    uniqueClients,
    projectManagerFilter,
    setProjectManagerFilter,
    uniqueManagers,
    projectStartDate,
    setProjectStartDate,
    projectEndDate,
    setProjectEndDate,
    clearProjectFilters,
    tableCardRef,
    tableCardStyle,
    loading,
    handleProjectSort,
    projectSortField,
    projectSortOrder,
    t,
    projectRowFilters,
    setProjectRowFilters,
    canViewCosts,
    clearProjectRowFilters,
    getPlannedHours,
    projectRealizedCostByWorkflowId,
    currentAverageHourlyRate,
    collectionTotalsTryByWorkflowInstanceId,
    getProjectFinanceSummaryForDashboard,
    getProjectCode,
    users,
    formatCurrencyByCode,
    getProjectCurrencyForFormData,
    convertTryAmountForProject,
    convertTryAmountToCurrency,
    convertAmountToTry,
    companySettings,
    applyCompanySettings,
    handleViewProjectFiles,
    navigate,
    handleEditProject,
    projectSummaryMode,
    setProjectSummaryMode,
    formatHoursTr,
    hasActiveProjectFilters,
    projectCurrentPage,
    setProjectCurrentPage,
    projectItemsPerPage,
    setProjectItemsPerPage,
  } = props;

  const [isProjectTableConfiguratorOpen, setIsProjectTableConfiguratorOpen] =
    useState(false);
  const [isSavingProjectTableLayout, setIsSavingProjectTableLayout] =
    useState(false);
  const projectLayoutBootstrapSignatureRef = useRef<string | null>(null);

  const labels = useMemo(
    () => ({
      search: t("common:search", { defaultValue: "Ara" }),
      searchPlaceholder: t("common:searchPlaceholder"),
      client: t("projects:client"),
      projectManager: t("projects:projectManager"),
      selected: t("common:selected"),
      all: t("common:all", { defaultValue: "Tümü" }),
      active: t("common:active", { defaultValue: "Aktif" }),
      archived: t("common:archived", { defaultValue: "Arşiv" }),
      startDate: t("dashboard:startDate"),
      endDate: t("dashboard:endDate"),
      projects: t("dashboard:managerProjects.projects"),
      noProjects: t("dashboard:managerProjects.noProjects"),
      noProjectsHint: t("dashboard:managerProjects.noProjectsHint"),
      projectName: t("projects:projectName"),
      clientEmployer: t("projects:clientEmployer"),
      realizedCost: t("dashboard:managerProjects.realizedCost"),
      spentHours: t("dashboard:managerProjects.spentHours", {
        defaultValue: "Harcanan",
      }),
      plannedHours: t("dashboard:managerProjects.plannedHours", {
        defaultValue: "Planlanan",
      }),
      invoiceTotal: t(
        "projects:workflowInstanceDetail.finance.invoiceTotal",
        { defaultValue: "Toplam Tahsil Edilecek Tutar" },
      ),
      netProfit: t("projects:workflowInstanceDetail.finance.netProfit", {
        defaultValue: "Gerçekleşen Net Kâr",
      }),
      estimatedNetProfit: t(
        "projects:workflowInstanceDetail.finance.estimatedNetProfit",
        { defaultValue: "Toplam Tahmini Net Kâr" },
      ),
      totalCollected: t(
        "projects:workflowInstanceDetail.finance.totalCollection",
        { defaultValue: "Tahsil Edilen" },
      ),
      remainingCollection: t(
        "projects:workflowInstanceDetail.finance.remainingCollection",
        { defaultValue: "Kalan Tahsilat" },
      ),
      estimatedTotal: t("dashboard:managerProjects.estimatedTotalCost", {
        defaultValue: "Tahmini Toplam Maliyet",
      }),
      files: t("dashboard:managerProjects.files"),
      total: t("dashboard:managerReports.total", { defaultValue: "Toplam" }),
      average: t("dashboard:managerReports.average", {
        defaultValue: "Ortalama",
      }),
      recordCount: t("dashboard:managerProjects.recordCount", {
        defaultValue: "kayıt",
      }),
      recordsFound: t("dashboard:managerProjects.recordsFound", {
        defaultValue: "kayıt bulundu",
      }),
      totalRecordsFrom: t("dashboard:managerProjects.totalRecordsFrom", {
        defaultValue: "toplam kayıttan",
      }),
      downloadExcel: t("dashboard:managerReports.excelDownload", {
        defaultValue: "Excel İndir",
      }),
      tableEdit: t("dashboard:managerProjects.tableEdit", {
        defaultValue: "Tabloyu Düzenle",
      }),
      shortEdit: t("dashboard:managerProjects.shortEdit", {
        defaultValue: "Düzenle",
      }),
      tableConfiguratorTitle: t(
        "dashboard:managerProjects.tableConfiguratorTitle",
        { defaultValue: "Proje Tablosu Kolonları" },
      ),
      tableConfiguratorDescription: t(
        "dashboard:managerProjects.tableConfiguratorDescription",
        {
          defaultValue:
            "Projeler sekmesinde görünecek sabit ve proje formu kolonlarını seçin.",
        },
      ),
      layoutSaved: t("dashboard:managerProjects.layoutSaved", {
        defaultValue: "Proje tablo düzeni kaydedildi.",
      }),
      selectedColumnsTitle: t("dashboard:managerProjects.selectedColumnsTitle", {
        defaultValue: "Seçili Kolonlar",
      }),
      defaultColumnsTitle: t("dashboard:managerProjects.defaultColumnsTitle", {
        defaultValue: "Varsayılan Kolonlar",
      }),
      projectStartFieldsTitle: t(
        "dashboard:managerProjects.projectStartFieldsTitle",
        {
          defaultValue: "Proje Başlangıç Alanları",
        },
      ),
      projectFieldColumnLabel: t(
        "dashboard:managerProjects.projectFieldColumnLabel",
        {
          defaultValue: "Proje alanı",
        },
      ),
      defaultColumnLabel: t("dashboard:managerProjects.defaultColumnLabel", {
        defaultValue: "Sabit kolon",
      }),
      showAll: t("dashboard:managerProjects.showAll", {
        defaultValue: "Tümünü Göster",
      }),
      projectNamePlaceholder: t("dashboard:managerProjects.projectNamePlaceholder"),
      projectCodePlaceholder: t("dashboard:managerProjects.projectCodePlaceholder"),
      projectManagerPlaceholder: t(
        "dashboard:managerProjects.projectManagerPlaceholder",
      ),
      loadingData: t("dashboard:managerProjects.loadingData"),
      noProjectsForFilter: t("dashboard:managerProjects.noProjectsForFilter"),
      unnamedProject: t("dashboard:managerProjects.unnamedProject"),
      clearRowFiltersTitle: t("dashboard:managerProjects.clearRowFiltersTitle"),
      minHoursPlaceholder: t("dashboard:managerProjects.minHoursPlaceholder", {
        defaultValue: "Min saat",
      }),
      minPlaceholder: t("dashboard:managerProjects.minPlaceholder", {
        defaultValue: "Min",
      }),
      realizedHours: t("dashboard:managerProjects.realizedHours", {
        defaultValue: "Gerçekleşen Saat",
      }),
      targetHours: t("dashboard:managerProjects.targetHours", {
        defaultValue: "Hedef Saat",
      }),
      remainingTargetHours: t(
        "dashboard:managerProjects.remainingTargetHours",
        { defaultValue: "Hedeften Kalan Saat" },
      ),
      remainingEstimatedCost: t(
        "dashboard:managerProjects.remainingEstimatedCost",
        { defaultValue: "Kalan Maliyet Tahmini" },
      ),
      vat: t("dashboard:managerProjects.vat", { defaultValue: "KDV" }),
      sort: t("dashboard:managerActivities.sort", { defaultValue: "Sırala" }),
      mixedCurrency: t("dashboard:managerProjects.mixedCurrency", {
        defaultValue: "Karma Para Birimi",
      }),
    }),
    [t],
  );

  const projectWorkflowOptions = useMemo(
    () => [
      { value: "all", label: workflowText },
      ...uniqueProjectWorkflows.map((workflow: string) => ({
        value: workflow,
        label: workflow,
      })),
    ],
    [uniqueProjectWorkflows, workflowText],
  );

  const projectArchiveOptions = useMemo(
    () => [
      {
        value: "all",
        label: `${labels.all} (${labels.active} + ${labels.archived})`,
      },
      { value: "active", label: labels.active },
      { value: "archived", label: labels.archived },
    ],
    [labels.active, labels.all, labels.archived],
  );

  const projectClientOptions = useMemo(
    () => [
      { value: "all", label: labels.client },
      ...uniqueClients.map((client: string) => ({
        value: client,
        label: client,
      })),
    ],
    [labels.client, uniqueClients],
  );

  const projectManagerOptions = useMemo(
    () => [
      { value: "all", label: labels.projectManager },
      ...uniqueManagers.map((manager: string) => ({
        value: manager,
        label: manager,
      })),
    ],
    [labels.projectManager, uniqueManagers],
  );

  const userById = useMemo(
    () => new Map<number, any>(users.map((user: any) => [Number(user.id), user])),
    [users],
  );

  const getProjectManagerName = useCallback(
    (projectManagerId: unknown) => {
      const managerId = Number(projectManagerId);
      if (!Number.isFinite(managerId)) {
        return "-";
      }
      const manager = userById.get(managerId);
      return manager ? `${manager.firstName} ${manager.lastName}` : "-";
    },
    [userById],
  );

  const formatMoneyValue = useCallback(
    (value: number | null | undefined, currencyCode: string) => {
      if (value === null || value === undefined || !Number.isFinite(value)) {
        return "-";
      }
      return formatCurrencyByCode(value, currencyCode, { display: "suffix" });
    },
    [formatCurrencyByCode],
  );

  const projectDefaultColumns = useMemo<ActivityTableColumnPreference[]>(() => {
    const baseColumns: ActivityTableColumnPreference[] = [
      { id: "projectName", kind: "default", label: labels.projectName },
      { id: "projectCode", kind: "default", label: t("projects:projectCode") },
      { id: "createdAt", kind: "default", label: t("dashboard:createdAt") },
      { id: "workflowName", kind: "default", label: workflowText },
      { id: "clientName", kind: "default", label: labels.clientEmployer },
      { id: "managerName", kind: "default", label: labels.projectManager },
      { id: "startDate", kind: "default", label: labels.startDate },
      { id: "endDate", kind: "default", label: labels.endDate },
      { id: "spentHours", kind: "default", label: labels.realizedHours },
      { id: "plannedHours", kind: "default", label: labels.targetHours },
      {
        id: "remainingHours",
        kind: "default",
        label: labels.remainingTargetHours,
      },
    ];

    if (!canViewCosts) {
      return baseColumns;
    }

    return [
      ...baseColumns,
      {
        id: "projectAmount",
        kind: "default",
        label:
          t("projects:offerAmount")
            .split("/")[0]
            ?.trim() || t("projects:offerAmount"),
      },
      { id: "vatAmount", kind: "default", label: labels.vat },
      { id: "invoiceTotal", kind: "default", label: labels.invoiceTotal },
      { id: "totalCollected", kind: "default", label: labels.totalCollected },
      {
        id: "remainingCollection",
        kind: "default",
        label: labels.remainingCollection,
      },
      { id: "realizedCost", kind: "default", label: labels.realizedCost },
      {
        id: "remainingEstimatedCost",
        kind: "default",
        label: labels.remainingEstimatedCost,
      },
      {
        id: "totalEstimatedCost",
        kind: "default",
        label: labels.estimatedTotal,
      },
      { id: "netProfit", kind: "default", label: labels.netProfit },
      {
        id: "estimatedNetProfit",
        kind: "default",
        label: labels.estimatedNetProfit,
      },
    ];
  }, [canViewCosts, labels, t, workflowText]);

  const storedProjectTableLayout = useMemo<ActivityTableColumnPreference[]>(() => {
    const raw =
      companySettings?.activityTableLayouts?.[PROJECT_TABLE_SCREEN_KEY];
    if (!Array.isArray(raw)) {
      return [];
    }

    return dedupeActivityTableColumns(
      raw
        .map((column) => {
          const id = typeof column?.id === "string" ? column.id.trim() : "";
          const kind = column?.kind === "form" ? "form" : "default";
          const label =
            typeof column?.label === "string" ? column.label.trim() : "";

          if (!id || !label) {
            return null;
          }

          return {
            id,
            kind,
            label,
          } as ActivityTableColumnPreference;
        })
        .filter((item): item is ActivityTableColumnPreference => item !== null),
    );
  }, [companySettings]);

  const storedProjectTableLayoutDefaultVersion = useMemo(() => {
    const rawVersion =
      companySettings?.activityTableLayoutDefaultVersions?.[
        PROJECT_TABLE_SCREEN_KEY
      ];
    const parsedVersion = Number(rawVersion);
    return Number.isFinite(parsedVersion) && parsedVersion >= 0
      ? parsedVersion
      : 0;
  }, [companySettings]);

  const projectAvailableFormColumns = useMemo<ProjectTableColumnPreference[]>(
    () => collectProjectFormColumns(projects),
    [projects],
  );

  const defaultProjectFormColumns = useMemo<ProjectTableColumnPreference[]>(
    () =>
      DEFAULT_PROJECT_FORM_COLUMN_LABELS.map((label) =>
        projectAvailableFormColumns.find(
          (column) =>
            normalizeProjectFormColumnSemanticLabel(column.label) ===
            normalizeProjectFormColumnSemanticLabel(label),
        ) || createProjectFormColumnPreference(label),
      ),
    [projectAvailableFormColumns],
  );

  const ensureProjectNameColumn = useCallback(
    (
      columns: ProjectTableColumnPreference[],
    ): ProjectTableColumnPreference[] => {
      const projectNameColumn =
        projectDefaultColumns.find((column) => column.id === "projectName") || null;
      if (!projectNameColumn) {
        return columns;
      }

      return dedupeActivityTableColumns([
        projectNameColumn,
        ...columns.filter((column) => column.id !== "projectName"),
      ]) as ProjectTableColumnPreference[];
    },
    [projectDefaultColumns],
  );

  const mergeProjectTableColumnsWithDefaultFormColumns = useCallback(
    (
      columns: ProjectTableColumnPreference[],
    ): ProjectTableColumnPreference[] => {
      if (!defaultProjectFormColumns.length) {
        return ensureProjectNameColumn(
          dedupeActivityTableColumns(
            columns,
          ) as ProjectTableColumnPreference[],
        );
      }

      const preferredIds = new Set(
        defaultProjectFormColumns.map((column) => column.id),
      );
      const columnsWithoutPreferred = columns.filter(
        (column) => !(column.kind === "form" && preferredIds.has(column.id)),
      );
      const managerIndex = columnsWithoutPreferred.findIndex(
        (column) => column.id === "managerName",
      );
      const insertIndex =
        managerIndex >= 0 ? managerIndex + 1 : columnsWithoutPreferred.length;

      return ensureProjectNameColumn(
        dedupeActivityTableColumns([
          ...columnsWithoutPreferred.slice(0, insertIndex),
          ...defaultProjectFormColumns,
          ...columnsWithoutPreferred.slice(insertIndex),
        ]) as ProjectTableColumnPreference[],
      );
    },
    [defaultProjectFormColumns, ensureProjectNameColumn],
  );

  const resolvedStoredProjectTableLayout = useMemo<ProjectTableColumnPreference[]>(
    () => {
      if (!storedProjectTableLayout.length) {
        return [];
      }

      const defaultMap = new Map(
        projectDefaultColumns.map((column) => [column.id, column]),
      );
      const formMap = new Map(
        projectAvailableFormColumns.map((column) => [column.id, column]),
      );

      return storedProjectTableLayout
        .map((column) =>
          column.kind === "form"
            ? formMap.get(column.id) ||
              projectAvailableFormColumns.find(
                (availableColumn) =>
                  normalizeProjectFormColumnSemanticLabel(
                    availableColumn.label,
                  ) === normalizeProjectFormColumnSemanticLabel(column.label),
              ) ||
              createProjectFormColumnPreference(column.label, {
                id: column.id,
              })
            : defaultMap.get(column.id) || null,
        )
        .filter(
          (column): column is ProjectTableColumnPreference => column !== null,
        );
    },
    [
      projectAvailableFormColumns,
      projectDefaultColumns,
      storedProjectTableLayout,
    ],
  );

  const missingDefaultProjectFormColumns = useMemo(
    () =>
      defaultProjectFormColumns.filter(
        (column) =>
          !resolvedStoredProjectTableLayout.some(
            (storedColumn) =>
              storedColumn.kind === "form" && storedColumn.id === column.id,
          ),
      ),
    [defaultProjectFormColumns, resolvedStoredProjectTableLayout],
  );

  const shouldBootstrapStoredProjectTableLayout =
    storedProjectTableLayout.length > 0 &&
    resolvedStoredProjectTableLayout.length > 0 &&
    missingDefaultProjectFormColumns.length > 0 &&
    storedProjectTableLayoutDefaultVersion <
      PROJECT_TABLE_DEFAULT_COLUMNS_VERSION;

  const projectVisibleColumns = useMemo<ProjectTableColumnPreference[]>(() => {
    if (!storedProjectTableLayout.length) {
      return mergeProjectTableColumnsWithDefaultFormColumns(
        projectDefaultColumns as ProjectTableColumnPreference[],
      );
    }

    if (!resolvedStoredProjectTableLayout.length) {
      return mergeProjectTableColumnsWithDefaultFormColumns(
        projectDefaultColumns as ProjectTableColumnPreference[],
      );
    }

    if (shouldBootstrapStoredProjectTableLayout) {
      return mergeProjectTableColumnsWithDefaultFormColumns(
        resolvedStoredProjectTableLayout,
      );
    }

    return ensureProjectNameColumn(
      dedupeActivityTableColumns(
        resolvedStoredProjectTableLayout,
      ) as ProjectTableColumnPreference[],
    );
  }, [
    ensureProjectNameColumn,
    mergeProjectTableColumnsWithDefaultFormColumns,
    projectDefaultColumns,
    resolvedStoredProjectTableLayout,
    shouldBootstrapStoredProjectTableLayout,
    storedProjectTableLayout.length,
  ]);

  type PersistProjectTableLayoutOptions = {
    closeConfigurator?: boolean;
    silent?: boolean;
  };

  const persistProjectTableLayout = useCallback(
    async (
      columns: ActivityTableColumnPreference[],
      options: PersistProjectTableLayoutOptions = {},
    ) => {
      const { closeConfigurator = true, silent = false } = options;
      const projectNameColumn =
        projectDefaultColumns.find((column) => column.id === "projectName") || null;
      const nextColumns = dedupeActivityTableColumns(
        projectNameColumn
          ? [projectNameColumn, ...columns.filter((column) => column.id !== "projectName")]
          : columns,
      );

      try {
        setIsSavingProjectTableLayout(true);
        const response = await apiService.updateActivityTableLayout(
          PROJECT_TABLE_SCREEN_KEY,
          nextColumns,
          PROJECT_TABLE_DEFAULT_COLUMNS_VERSION,
        );
        const settings = (response?.data || response) as Record<string, any>;
        applyCompanySettings?.(settings);
        if (closeConfigurator) {
          setIsProjectTableConfiguratorOpen(false);
        }
        if (!silent) {
          toast.success(labels.layoutSaved);
        }
      } catch (error) {
        if (!silent) {
          toast.error(t("errors:generic"));
        }
      } finally {
        setIsSavingProjectTableLayout(false);
      }
    },
    [applyCompanySettings, labels.layoutSaved, projectDefaultColumns, t],
  );

  useEffect(() => {
    if (!shouldBootstrapStoredProjectTableLayout) {
      return;
    }

    const signature = [
      storedProjectTableLayoutDefaultVersion,
      resolvedStoredProjectTableLayout
        .map((column) => `${column.kind}:${column.id}`)
        .join(","),
      missingDefaultProjectFormColumns.map((column) => column.id).join(","),
    ].join("|");

    if (projectLayoutBootstrapSignatureRef.current === signature) {
      return;
    }

    projectLayoutBootstrapSignatureRef.current = signature;

    void persistProjectTableLayout(
      mergeProjectTableColumnsWithDefaultFormColumns(
        resolvedStoredProjectTableLayout,
      ),
      {
        closeConfigurator: false,
        silent: true,
      },
    );
  }, [
    mergeProjectTableColumnsWithDefaultFormColumns,
    missingDefaultProjectFormColumns,
    persistProjectTableLayout,
    resolvedStoredProjectTableLayout,
    shouldBootstrapStoredProjectTableLayout,
    storedProjectTableLayoutDefaultVersion,
  ]);

  const baseProjectItems = useMemo(
    () =>
      filteredProjects.map((project: any) => {
        const formData = parseProjectFormData(project);
        const projectName =
          formData.name || formData.projectName || labels.unnamedProject;
        const clientName = formData.clientName || formData.client || "-";
        const managerName = getProjectManagerName(formData.projectManager);
        const projectCode = getProjectCode(project, formData) || "-";
        const rawSpentHours = Number(project?.totalSpentHours);
        const spentHours =
          Number.isFinite(rawSpentHours) && rawSpentHours > 0 ? rawSpentHours : 0;

        return {
          project,
          formData,
          projectName,
          clientName,
          managerName,
          projectCode,
          spentHours,
          containerFormElements: getProjectContainerFormElements(project),
        };
      }),
    [
      filteredProjects,
      getProjectCode,
      getProjectManagerName,
      labels.unnamedProject,
    ],
  );

  const getProjectMetrics = useCallback(
    (item: any) => {
      const plannedHours = getPlannedHours(item.formData);
      const hasPlannedHours = Number.isFinite(plannedHours) && plannedHours > 0;
      const remainingHoursRaw = hasPlannedHours
        ? plannedHours - item.spentHours
        : null;
      const remainingHours = hasPlannedHours
        ? Math.max(plannedHours - item.spentHours, 0)
        : 0;
      const projectCurrency = getProjectCurrencyForFormData(item.formData);
      const realizedCostTry =
        projectRealizedCostByWorkflowId.get(item.project.id) ?? 0;
      const totalCollectedTry =
        collectionTotalsTryByWorkflowInstanceId.get(Number(item.project.id)) ?? null;
      const remainingEstimatedCostTry =
        currentAverageHourlyRate !== null
          ? remainingHours * currentAverageHourlyRate
          : null;
      const realizedCost = convertTryAmountForProject(
        item.formData,
        realizedCostTry,
      );
      const remainingEstimatedCost =
        remainingEstimatedCostTry !== null
          ? convertTryAmountForProject(item.formData, remainingEstimatedCostTry)
          : null;
      const totalEstimatedCost =
        realizedCost !== null && remainingEstimatedCost !== null
          ? realizedCost + remainingEstimatedCost
          : null;
      const totalEstimatedCostTry =
        remainingEstimatedCostTry !== null
          ? realizedCostTry + remainingEstimatedCostTry
          : null;
      const financeSummary = getProjectFinanceSummaryForDashboard(
        item.formData,
        realizedCostTry,
        totalEstimatedCostTry,
        totalCollectedTry,
      );

      return {
        plannedHours,
        hasPlannedHours,
        remainingHoursRaw,
        projectCurrency,
        realizedCostTry,
        remainingEstimatedCostTry,
        totalEstimatedCostTry,
        realizedCost,
        remainingEstimatedCost,
        totalEstimatedCost,
        financeSummary,
        vatAmount:
          financeSummary.projectAmount !== null &&
          financeSummary.invoiceTotal !== null
            ? financeSummary.invoiceTotal - financeSummary.projectAmount
            : null,
      };
    },
    [
      collectionTotalsTryByWorkflowInstanceId,
      convertTryAmountForProject,
      currentAverageHourlyRate,
      getPlannedHours,
      getProjectCurrencyForFormData,
      getProjectFinanceSummaryForDashboard,
      projectRealizedCostByWorkflowId,
    ],
  );

  const visibleProjectFormColumns = useMemo(
    () =>
      projectVisibleColumns.filter(
        (column) => column.kind === "form",
      ) as ProjectTableColumnPreference[],
    [projectVisibleColumns],
  );

  const projectFormFilterOptionsByColumnId = useMemo(() => {
    const next: Record<string, string[]> = {};

    visibleProjectFormColumns.forEach((column) => {
      const valueType = getProjectFormColumnValueType(column);
      if (valueType === "select" || valueType === "boolean") {
        next[column.id] = getProjectFormColumnFilterOptions(
          baseProjectItems,
          column,
          t,
        );
      }
    });

    return next;
  }, [baseProjectItems, t, visibleProjectFormColumns]);

  const filteredProjectItems = useMemo(() => {
    const compareNullableNumbers = (
      aValue: number | null,
      bValue: number | null,
    ): number => {
      if (aValue === null && bValue === null) {
        return 0;
      }

      if (aValue === null) {
        return 1;
      }

      if (bValue === null) {
        return -1;
      }

      return aValue - bValue;
    };

    const next = baseProjectItems.filter((item: any) =>
      visibleProjectFormColumns.every((column: ProjectTableColumnPreference) => {
        const filterValue = String(projectRowFilters?.[column.id] || "").trim();

        if (!filterValue) {
          return true;
        }

        const columnValueType = getProjectFormColumnValueType(column);

        if (columnValueType === "number") {
          const minValue = parseProjectFormNumericValue(filterValue);
          if (minValue === null) {
            return true;
          }

          const projectValue = getProjectFormColumnNumericValue(item, column, t);
          return projectValue !== null && projectValue >= minValue;
        }

        if (columnValueType === "date") {
          const projectValue = getProjectFormColumnDateValue(item, column, t);
          return projectValue !== null
            ? new Date(projectValue).toISOString().slice(0, 10) === filterValue
            : false;
        }

        const normalizedFilter = normalizeActivityTableLabel(filterValue);
        const normalizedValue = normalizeActivityTableLabel(
          getProjectFormColumnValue(item, column, t),
        );

        if (columnValueType === "select" || columnValueType === "boolean") {
          return normalizedValue === normalizedFilter;
        }

        return normalizedValue.includes(normalizedFilter);
      }),
    );

    if (
      projectSortOrder &&
      typeof projectSortField === "string" &&
      projectSortField.startsWith("form:")
    ) {
      next.sort((a: any, b: any) => {
        const column = visibleProjectFormColumns.find(
          (item: ProjectTableColumnPreference) => item.id === projectSortField,
        );

        if (!column) {
          return 0;
        }

        const columnValueType = getProjectFormColumnValueType(column);
        let comparison = 0;

        if (columnValueType === "number") {
          comparison = compareNullableNumbers(
            getProjectFormColumnNumericValue(a, column, t),
            getProjectFormColumnNumericValue(b, column, t),
          );
        } else if (columnValueType === "date") {
          comparison = compareNullableNumbers(
            getProjectFormColumnDateValue(a, column, t),
            getProjectFormColumnDateValue(b, column, t),
          );
        } else {
          const aValue = normalizeActivityTableLabel(
            getProjectFormColumnValue(a, column, t),
          );
          const bValue = normalizeActivityTableLabel(
            getProjectFormColumnValue(b, column, t),
          );
          comparison = aValue.localeCompare(bValue, "tr-TR");
        }

        return projectSortOrder === "asc" ? comparison : -comparison;
      });
    }

    return next;
  }, [
    baseProjectItems,
    projectRowFilters,
    projectSortField,
    projectSortOrder,
    t,
    visibleProjectFormColumns,
  ]);

  const projectItemsWithMetrics = useMemo(
    () =>
      filteredProjectItems.map((item: any) => ({
        ...item,
        metrics: getProjectMetrics(item),
      })),
    [filteredProjectItems, getProjectMetrics],
  );

  const summaryStats = useMemo(() => {
    const projectCurrencies = new Set<string>();
    const projectAmount = createMetricAccumulator();
    const invoiceTotal = createMetricAccumulator();
    const totalCollected = createMetricAccumulator();
    const remainingCollection = createMetricAccumulator();
    const netProfit = createMetricAccumulator();
    const estimatedNetProfit = createMetricAccumulator();
    let spentHours = 0;
    let plannedHours = 0;
    let remainingHours = 0;
    let hasPlannedHours = false;
    let realizedCostTry = 0;
    let hasRealizedCost = false;
    let remainingEstimatedCostTry = 0;
    let hasRemainingEstimatedCost = false;
    let totalEstimatedCostTry = 0;
    let hasTotalEstimatedCost = false;

    const addConvertedMetric = (
      accumulator: MetricAccumulator,
      value: number | null | undefined,
      currencyCode: string,
    ) => {
      if (value === null || value === undefined || !Number.isFinite(value)) {
        return;
      }
      accumulator.hasValue = true;
      const converted = convertAmountToTry(value, currencyCode);
      if (converted === null) {
        accumulator.missingConversion = true;
        return;
      }
      accumulator.sum += converted;
    };

    projectItemsWithMetrics.forEach((item: any) => {
      const metrics = item.metrics;
      if (metrics.projectCurrency) {
        projectCurrencies.add(metrics.projectCurrency);
      }

      spentHours += item.spentHours;

      if (metrics.hasPlannedHours) {
        plannedHours += metrics.plannedHours;
        if (metrics.remainingHoursRaw !== null) {
          remainingHours += metrics.remainingHoursRaw;
        }
        hasPlannedHours = true;
      }

      addConvertedMetric(
        projectAmount,
        metrics.financeSummary.projectAmount,
        metrics.projectCurrency,
      );
      addConvertedMetric(
        invoiceTotal,
        metrics.financeSummary.invoiceTotal,
        metrics.projectCurrency,
      );
      addConvertedMetric(
        totalCollected,
        metrics.financeSummary.totalCollected,
        metrics.projectCurrency,
      );
      addConvertedMetric(
        remainingCollection,
        metrics.financeSummary.remainingCollection,
        metrics.projectCurrency,
      );
      addConvertedMetric(netProfit, metrics.financeSummary.netProfit, metrics.projectCurrency);
      addConvertedMetric(
        estimatedNetProfit,
        metrics.financeSummary.estimatedNetProfit,
        metrics.projectCurrency,
      );

      hasRealizedCost = true;
      realizedCostTry += metrics.realizedCostTry;

      if (metrics.remainingEstimatedCostTry !== null) {
        hasRemainingEstimatedCost = true;
        remainingEstimatedCostTry += metrics.remainingEstimatedCostTry;
      }

      if (metrics.totalEstimatedCostTry !== null) {
        hasTotalEstimatedCost = true;
        totalEstimatedCostTry += metrics.totalEstimatedCostTry;
      }
    });

    const count = projectItemsWithMetrics.length;
    const summaryCurrency =
      projectCurrencies.size === 1 ? Array.from(projectCurrencies)[0] : null;
    const summaryDisplayCurrency =
      summaryCurrency || (projectCurrencies.size > 1 ? "TRY" : null);
    const divisor = projectSummaryMode === "average" && count > 0 ? count : 1;

    const convertTryMetric = (
      totalTry: number,
      hasValue: boolean,
      missingConversion = false,
    ): number | null => {
      if (!hasValue || missingConversion || !summaryDisplayCurrency) {
        return null;
      }

      const averageTry = totalTry / divisor;
      if (summaryDisplayCurrency === "TRY") {
        return averageTry;
      }

      return convertTryAmountToCurrency(averageTry, summaryDisplayCurrency);
    };

    return {
      count,
      summaryDisplayCurrency,
      spentHours: count > 0 ? spentHours / divisor : 0,
      plannedHours: hasPlannedHours ? plannedHours / divisor : null,
      remainingHours: hasPlannedHours ? remainingHours / divisor : null,
      projectAmount: convertTryMetric(
        projectAmount.sum,
        projectAmount.hasValue,
        projectAmount.missingConversion,
      ),
      invoiceTotal: convertTryMetric(
        invoiceTotal.sum,
        invoiceTotal.hasValue,
        invoiceTotal.missingConversion,
      ),
      totalCollected: convertTryMetric(
        totalCollected.sum,
        totalCollected.hasValue,
        totalCollected.missingConversion,
      ),
      remainingCollection: convertTryMetric(
        remainingCollection.sum,
        remainingCollection.hasValue,
        remainingCollection.missingConversion,
      ),
      realizedCost: convertTryMetric(realizedCostTry, hasRealizedCost),
      remainingEstimatedCost: convertTryMetric(
        remainingEstimatedCostTry,
        hasRemainingEstimatedCost,
      ),
      totalEstimatedCost: convertTryMetric(
        totalEstimatedCostTry,
        hasTotalEstimatedCost,
      ),
      netProfit: convertTryMetric(
        netProfit.sum,
        netProfit.hasValue,
        netProfit.missingConversion,
      ),
      estimatedNetProfit: convertTryMetric(
        estimatedNetProfit.sum,
        estimatedNetProfit.hasValue,
        estimatedNetProfit.missingConversion,
      ),
    };
  }, [
    convertAmountToTry,
    convertTryAmountToCurrency,
    projectItemsWithMetrics,
    projectSummaryMode,
  ]);

  const summaryVatAmount =
    summaryStats.projectAmount !== null && summaryStats.invoiceTotal !== null
      ? summaryStats.invoiceTotal - summaryStats.projectAmount
      : null;

  const hasActiveDynamicFormFilters = visibleProjectFormColumns.some((column) =>
    String(projectRowFilters?.[column.id] || "").trim(),
  );

  const isShowAllProjects = projectItemsPerPage <= 0;
  const projectTotalPages = isShowAllProjects
    ? 1
    : Math.max(1, Math.ceil(projectItemsWithMetrics.length / projectItemsPerPage));

  useEffect(() => {
    if (projectCurrentPage > projectTotalPages) {
      setProjectCurrentPage(projectTotalPages);
    }
  }, [projectCurrentPage, projectTotalPages, setProjectCurrentPage]);

  const projectPageStartIndex = isShowAllProjects
    ? 0
    : (projectCurrentPage - 1) * projectItemsPerPage;

  const paginatedProjectItems = isShowAllProjects
    ? projectItemsWithMetrics
    : projectItemsWithMetrics.slice(
        projectPageStartIndex,
        projectPageStartIndex + projectItemsPerPage,
      );

  const {
    scrollContainerRef,
    setScrollTop,
    virtualizationEnabled,
    startIndex: virtualStartIndex,
    endIndex: virtualEndIndex,
    topSpacerHeight,
    bottomSpacerHeight,
  } = useVirtualizedRows({
    itemCount: paginatedProjectItems.length,
    rowHeight: 40,
    overscan: 10,
    enabled: !loading && paginatedProjectItems.length > 80,
    resetKey: `${projectCurrentPage}-${projectItemsPerPage}-${paginatedProjectItems.length}`,
  });

  const visiblePaginatedProjectItems = useMemo(
    () =>
      virtualizationEnabled
        ? paginatedProjectItems.slice(virtualStartIndex, virtualEndIndex)
        : paginatedProjectItems,
    [
      paginatedProjectItems,
      virtualEndIndex,
      virtualStartIndex,
      virtualizationEnabled,
    ],
  );

  const getSortFieldForColumn = useCallback(
    (column: ProjectTableColumnPreference) =>
      column.kind === "form"
        ? column.id
        : PROJECT_SORT_FIELD_BY_COLUMN_ID[column.id] || column.id,
    [],
  );

  const setRowFilterValue = useCallback(
    (key: string, value: string) => {
      setProjectRowFilters((prev: Record<string, string>) => ({
        ...prev,
        [key]: value,
      }));
    },
    [setProjectRowFilters],
  );

  const exportProjectsToExcel = useCallback(() => {
    if (!projectItemsWithMetrics.length) {
      return;
    }

    const headers = projectVisibleColumns.map((column) => column.label);
    const rows: Array<Array<string | number>> = [headers];

    projectItemsWithMetrics.forEach((item: any) => {
      const metrics = item.metrics;
      const row = projectVisibleColumns.map(
        (column: ProjectTableColumnPreference) => {
        if (column.kind === "form") {
          return getProjectFormColumnValue(item, column, t) || "-";
        }

        switch (column.id) {
          case "projectName":
            return item.projectName;
          case "projectCode":
            return item.projectCode;
          case "createdAt":
            return formatProjectDate(item.project.createdAt);
          case "workflowName":
            return item.project.templateWorkflowName || "-";
          case "clientName":
            return item.clientName;
          case "managerName":
            return item.managerName;
          case "startDate":
            return formatProjectDate(item.formData.startDate);
          case "endDate":
            return formatProjectDate(item.formData.endDate);
          case "spentHours":
            return formatHoursTr(item.spentHours);
          case "plannedHours":
            return metrics.hasPlannedHours ? formatHoursTr(metrics.plannedHours) : "-";
          case "remainingHours":
            return metrics.remainingHoursRaw !== null
              ? formatHoursTr(metrics.remainingHoursRaw)
              : "-";
          case "projectAmount":
            return formatMoneyValue(
              metrics.financeSummary.projectAmount,
              metrics.projectCurrency,
            );
          case "vatAmount":
            return formatMoneyValue(metrics.vatAmount, metrics.projectCurrency);
          case "invoiceTotal":
            return formatMoneyValue(
              metrics.financeSummary.invoiceTotal,
              metrics.projectCurrency,
            );
          case "totalCollected":
            return formatMoneyValue(
              metrics.financeSummary.totalCollected,
              metrics.projectCurrency,
            );
          case "remainingCollection":
            return formatMoneyValue(
              metrics.financeSummary.remainingCollection,
              metrics.projectCurrency,
            );
          case "realizedCost":
            return formatMoneyValue(metrics.realizedCost, metrics.projectCurrency);
          case "remainingEstimatedCost":
            return formatMoneyValue(
              metrics.remainingEstimatedCost,
              metrics.projectCurrency,
            );
          case "totalEstimatedCost":
            return formatMoneyValue(
              metrics.totalEstimatedCost,
              metrics.projectCurrency,
            );
          case "netProfit":
            return formatMoneyValue(
              metrics.financeSummary.netProfit,
              metrics.projectCurrency,
            );
          case "estimatedNetProfit":
            return formatMoneyValue(
              metrics.financeSummary.estimatedNetProfit,
              metrics.projectCurrency,
            );
        default:
          return "-";
        }
      });

      rows.push(row);
    });

    rows.push(
      projectVisibleColumns.map(
        (column: ProjectTableColumnPreference, index: number) => {
        if (index === 0) {
          return projectSummaryMode === "average"
            ? t("dashboard:managerActivities.filteredAverage", {
                defaultValue: "Ortalama (Filtre Sonucu)",
              })
            : t("dashboard:managerActivities.filteredTotal", {
                defaultValue: "Toplam (Filtre Sonucu)",
              });
        }

        if (index === 1) {
          return `${summaryStats.count} ${labels.recordCount}`;
        }

        switch (column.id) {
          case "spentHours":
            return formatHoursTr(summaryStats.spentHours);
          case "plannedHours":
            return summaryStats.plannedHours !== null
              ? formatHoursTr(summaryStats.plannedHours)
              : "-";
          case "remainingHours":
            return summaryStats.remainingHours !== null
              ? formatHoursTr(summaryStats.remainingHours)
              : "-";
          case "projectAmount":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.projectAmount,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "vatAmount":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(summaryVatAmount, summaryStats.summaryDisplayCurrency)
              : "-";
          case "invoiceTotal":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.invoiceTotal,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "totalCollected":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.totalCollected,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "remainingCollection":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.remainingCollection,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "realizedCost":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.realizedCost,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "remainingEstimatedCost":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.remainingEstimatedCost,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "totalEstimatedCost":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.totalEstimatedCost,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "netProfit":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.netProfit,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
          case "estimatedNetProfit":
            return summaryStats.summaryDisplayCurrency
              ? formatMoneyValue(
                  summaryStats.estimatedNetProfit,
                  summaryStats.summaryDisplayCurrency,
                )
              : "-";
        default:
          return "";
        }
      }),
    );

    exportRowsToExcel({
      fileName: `Projeler_${new Date().toISOString().slice(0, 10)}.xlsx`,
      sheetName: "Projeler",
      rows,
    });
  }, [
    formatHoursTr,
    formatMoneyValue,
    labels.recordCount,
    projectItemsWithMetrics,
    projectSummaryMode,
    projectVisibleColumns,
    summaryStats,
    summaryVatAmount,
    t,
  ]);

  const renderFilterCell = useCallback(
    (column: ProjectTableColumnPreference) => {
      if (column.kind === "form") {
        const columnValueType = getProjectFormColumnValueType(column);

        if (columnValueType === "select" || columnValueType === "boolean") {
          const options = projectFormFilterOptionsByColumnId[column.id] || [];

          return (
            <select
              value={projectRowFilters?.[column.id] || ""}
              onChange={(event) => setRowFilterValue(column.id, event.target.value)}
              className="w-full min-w-[120px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            >
              <option value="">{column.label}</option>
              {options.map((option) => (
                <option key={`${column.id}-${option}`} value={option}>
                  {option}
                </option>
              ))}
            </select>
          );
        }

        if (columnValueType === "date") {
          return (
            <input
              type="date"
              value={projectRowFilters?.[column.id] || ""}
              onChange={(event) => setRowFilterValue(column.id, event.target.value)}
              className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          );
        }

        if (columnValueType === "number") {
          return (
            <input
              type="number"
              inputMode="decimal"
              value={projectRowFilters?.[column.id] || ""}
              onChange={(event) => setRowFilterValue(column.id, event.target.value)}
              placeholder={labels.minPlaceholder}
              className="w-full min-w-[100px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
          );
        }

        return (
          <input
            type="text"
            value={projectRowFilters?.[column.id] || ""}
            onChange={(event) => setRowFilterValue(column.id, event.target.value)}
            placeholder={column.label}
            className="w-full min-w-[120px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        );
      }

      if (column.id === "projectName") {
        return (
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={projectRowFilters.projectName}
              onChange={(event) =>
                setRowFilterValue("projectName", event.target.value)
              }
              placeholder={labels.projectNamePlaceholder}
              className="w-full min-w-[280px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
            />
            <button
              type="button"
              onClick={clearProjectRowFilters}
              className="px-2 py-1 text-[10px] font-semibold rounded border border-purple-300 text-purple-700 hover:bg-purple-100 shrink-0"
              title={labels.clearRowFiltersTitle}
            >
              {t("common:clear")}
            </button>
          </div>
        );
      }

      if (column.id === "createdAt" || column.id === "startDate" || column.id === "endDate") {
        return (
          <input
            type="date"
            value={projectRowFilters?.[column.id] || ""}
            onChange={(event) => setRowFilterValue(column.id, event.target.value)}
            className="w-full min-w-[130px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        );
      }

      if (column.id === "projectCode") {
        return (
          <input
            type="text"
            value={projectRowFilters.projectCode}
            onChange={(event) => setRowFilterValue("projectCode", event.target.value)}
            placeholder={labels.projectCodePlaceholder}
            className="w-full min-w-[120px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        );
      }

      if (column.id === "workflowName" || column.id === "clientName" || column.id === "managerName") {
        const placeholder =
          column.id === "managerName"
            ? labels.projectManagerPlaceholder
            : column.label;
        return (
          <input
            type="text"
            value={projectRowFilters?.[column.id] || ""}
            onChange={(event) => setRowFilterValue(column.id, event.target.value)}
            placeholder={placeholder}
            className="w-full min-w-[120px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        );
      }

      const numericKey = PROJECT_NUMERIC_FILTER_KEY_BY_COLUMN_ID[column.id];
      if (numericKey) {
        return (
          <input
            type="number"
            value={projectRowFilters?.[numericKey] || ""}
            onChange={(event) => setRowFilterValue(numericKey, event.target.value)}
            placeholder={
              column.id === "spentHours"
                ? labels.minHoursPlaceholder
                : labels.minPlaceholder
            }
            className="w-full min-w-[100px] px-2 py-1 text-[11px] border border-purple-200 rounded focus:outline-none focus:ring-1 focus:ring-purple-500"
          />
        );
      }

      return null;
    },
    [
      clearProjectRowFilters,
      labels.clearRowFiltersTitle,
      labels.minHoursPlaceholder,
      labels.minPlaceholder,
      labels.projectCodePlaceholder,
      labels.projectManagerPlaceholder,
      labels.projectNamePlaceholder,
      projectFormFilterOptionsByColumnId,
      projectRowFilters,
      setRowFilterValue,
      t,
    ],
  );

  const renderProjectCell = useCallback(
    (item: any, column: ProjectTableColumnPreference) => {
      const metrics = item.metrics;

      if (column.kind === "form") {
        const value = getProjectFormColumnValue(item, column, t);
        return (
          <OverflowInfoCell
            value={value || "-"}
            fullValue={value}
            title={column.label}
            threshold={24}
          />
        );
      }

      switch (column.id) {
        case "projectName":
          return (
            <div className="flex items-center justify-between gap-3 min-w-0 overflow-hidden">
              <div className="min-w-0 flex-1">
                <OverflowInfoCell
                  value={item.projectName}
                  title={labels.projectName}
                  threshold={28}
                  portalTooltip
                />
              </div>
              <div className="flex items-center gap-1.5 justify-end shrink-0">
                <button
                  onClick={() => handleViewProjectFiles(item.project, item.formData)}
                  className="inline-flex items-center justify-center p-1.5 text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                  title={labels.files}
                  aria-label={labels.files}
                >
                  <FolderIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() =>
                    navigate(`/project-instances/${item.project.id}`, {
                      state: {
                        backTo: "/manager-dashboard",
                        backState: { mainTab: "projects" },
                      },
                    })
                  }
                  className="inline-flex items-center justify-center p-1.5 text-white bg-indigo-600 hover:bg-indigo-700 rounded-md transition-colors"
                  title={t("common:details")}
                  aria-label={t("common:details")}
                >
                  <EyeIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={() => handleEditProject(item.project)}
                  className="inline-flex items-center justify-center p-1.5 text-white bg-orange-500 hover:bg-orange-600 rounded-md transition-colors"
                  title={t("common:edit")}
                  aria-label={t("common:edit")}
                >
                  <PencilSquareIcon className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          );
        case "projectCode":
          return item.projectCode;
        case "createdAt":
          return formatProjectDate(item.project.createdAt);
        case "workflowName":
          return (
            <OverflowInfoCell
              value={item.project.templateWorkflowName || "-"}
              fullValue={item.project.templateWorkflowName || ""}
              title={workflowText}
              threshold={24}
            />
          );
        case "clientName":
          return (
            <OverflowInfoCell
              value={item.clientName}
              fullValue={item.clientName === "-" ? "" : item.clientName}
              title={labels.clientEmployer}
              threshold={24}
            />
          );
        case "managerName":
          return (
            <OverflowInfoCell
              value={item.managerName}
              fullValue={item.managerName === "-" ? "" : item.managerName}
              title={labels.projectManager}
              threshold={24}
            />
          );
        case "startDate":
          return formatProjectDate(item.formData.startDate);
        case "endDate":
          return formatProjectDate(item.formData.endDate);
        case "spentHours":
          return formatHoursTr(item.spentHours);
        case "plannedHours":
          return metrics.hasPlannedHours ? formatHoursTr(metrics.plannedHours) : "-";
        case "remainingHours":
          return metrics.remainingHoursRaw !== null
            ? formatHoursTr(metrics.remainingHoursRaw)
            : "-";
        case "projectAmount":
          return formatMoneyValue(
            metrics.financeSummary.projectAmount,
            metrics.projectCurrency,
          );
        case "vatAmount":
          return formatMoneyValue(metrics.vatAmount, metrics.projectCurrency);
        case "invoiceTotal":
          return formatMoneyValue(
            metrics.financeSummary.invoiceTotal,
            metrics.projectCurrency,
          );
        case "totalCollected":
          return formatMoneyValue(
            metrics.financeSummary.totalCollected,
            metrics.projectCurrency,
          );
        case "remainingCollection":
          return formatMoneyValue(
            metrics.financeSummary.remainingCollection,
            metrics.projectCurrency,
          );
        case "realizedCost":
          return formatMoneyValue(metrics.realizedCost, metrics.projectCurrency);
        case "remainingEstimatedCost":
          return formatMoneyValue(
            metrics.remainingEstimatedCost,
            metrics.projectCurrency,
          );
        case "totalEstimatedCost":
          return formatMoneyValue(
            metrics.totalEstimatedCost,
            metrics.projectCurrency,
          );
        case "netProfit":
          return formatMoneyValue(
            metrics.financeSummary.netProfit,
            metrics.projectCurrency,
          );
        case "estimatedNetProfit":
          return formatMoneyValue(
            metrics.financeSummary.estimatedNetProfit,
            metrics.projectCurrency,
          );
        default:
          return "-";
      }
    },
    [
      formatHoursTr,
      formatMoneyValue,
      handleEditProject,
      handleViewProjectFiles,
      labels.clientEmployer,
      labels.files,
      labels.projectManager,
      labels.projectName,
      navigate,
      t,
      workflowText,
    ],
  );

  const renderSummaryCell = useCallback(
    (column: ProjectTableColumnPreference) => {
      const money = (value: number | null) =>
        summaryStats.summaryDisplayCurrency
          ? formatMoneyValue(value, summaryStats.summaryDisplayCurrency)
          : "-";

      switch (column.id) {
        case "projectName":
          return (
            <div className="flex items-center justify-between gap-3">
              <div className="inline-flex rounded-md border border-purple-200 overflow-hidden">
                <button
                  type="button"
                  onClick={() => setProjectSummaryMode("total")}
                  className={`px-2 py-0.5 text-[10px] font-semibold ${
                    projectSummaryMode === "total"
                      ? "bg-purple-600 text-white"
                      : "bg-white text-purple-700 hover:bg-purple-100"
                  }`}
                >
                  {labels.total}
                </button>
                <button
                  type="button"
                  onClick={() => setProjectSummaryMode("average")}
                  className={`px-2 py-0.5 text-[10px] font-semibold border-l border-purple-200 ${
                    projectSummaryMode === "average"
                      ? "bg-purple-600 text-white"
                      : "bg-white text-purple-700 hover:bg-purple-100"
                  }`}
                >
                  {labels.average}
                </button>
              </div>
              <span className="text-xs text-gray-700 font-medium">
                {summaryStats.count} {labels.recordCount}
              </span>
            </div>
          );
        case "spentHours":
          return formatHoursTr(summaryStats.spentHours);
        case "plannedHours":
          return summaryStats.plannedHours !== null
            ? formatHoursTr(summaryStats.plannedHours)
            : "-";
        case "remainingHours":
          return summaryStats.remainingHours !== null
            ? formatHoursTr(summaryStats.remainingHours)
            : "-";
        case "projectAmount":
          return money(summaryStats.projectAmount);
        case "vatAmount":
          return money(summaryVatAmount);
        case "invoiceTotal":
          return money(summaryStats.invoiceTotal);
        case "totalCollected":
          return money(summaryStats.totalCollected);
        case "remainingCollection":
          return money(summaryStats.remainingCollection);
        case "realizedCost":
          return money(summaryStats.realizedCost);
        case "remainingEstimatedCost":
          return money(summaryStats.remainingEstimatedCost);
        case "totalEstimatedCost":
          return money(summaryStats.totalEstimatedCost);
        case "netProfit":
          return money(summaryStats.netProfit);
        case "estimatedNetProfit":
          return money(summaryStats.estimatedNetProfit);
        default:
          return "";
      }
    },
    [
      formatHoursTr,
      formatMoneyValue,
      labels.average,
      labels.recordCount,
      labels.total,
      projectSummaryMode,
      setProjectSummaryMode,
      summaryStats,
      summaryVatAmount,
    ],
  );

  const visibleColumnCount = Math.max(projectVisibleColumns.length, 1);
  const combinedHasActiveFilters =
    hasActiveProjectFilters || hasActiveDynamicFormFilters;

  return (
    <div className="space-y-0">
      <div className="relative z-[60] bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-visible">
        <div className="bg-slate-50 border-b-2 border-gray-200 px-2.5 py-2 overflow-visible">
          <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-1 sm:overflow-x-auto lg:overflow-visible">
            <div className="relative col-span-2 min-w-0 sm:col-span-1 sm:w-[146px] sm:min-w-[126px] sm:shrink-0 xl:w-[168px]">
              <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-purple-400" />
              <input
                type="text"
                placeholder={labels.searchPlaceholder}
                value={projectSearchTerm}
                onChange={(event) => setProjectSearchTerm(event.target.value)}
                className="w-full pl-8 pr-2 py-1.5 text-[13px] bg-white/80 backdrop-blur-sm border-2 border-purple-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-400 shadow-sm transition-all font-medium"
              />
            </div>

            <div className="hidden lg:block h-8 w-px bg-gradient-to-b from-purple-200 via-pink-300 to-purple-200 shrink-0" />

            <SearchableFilterSelect value={projectArchiveFilter} onChange={setProjectArchiveFilter} options={projectArchiveOptions} searchPlaceholder={labels.search} emptyLabel={t("common:noResults")} title={labels.all} containerClassName="w-[120px] xl:w-[132px] min-w-[120px]" buttonClassName="border-violet-200 focus:ring-2 focus:ring-violet-500 focus:border-violet-400 font-semibold text-violet-700 hover:bg-violet-50" />
            <SearchableFilterSelect value={projectWorkflowFilter} onChange={setProjectWorkflowFilter} options={projectWorkflowOptions} searchPlaceholder={labels.search} emptyLabel={t("common:noResults")} title={workflowText} containerClassName="w-[136px] xl:w-[148px] min-w-[136px]" buttonClassName="border-blue-200 focus:ring-2 focus:ring-blue-500 focus:border-blue-400 font-semibold text-blue-700 hover:bg-blue-50" />
            <SearchableFilterSelect value={projectClientFilter} onChange={setProjectClientFilter} options={projectClientOptions} searchPlaceholder={labels.search} emptyLabel={t("common:noResults")} title={labels.client} containerClassName="w-[132px] xl:w-[144px] min-w-[132px]" buttonClassName="border-green-200 focus:ring-2 focus:ring-green-500 focus:border-green-400 font-semibold text-green-700 hover:bg-green-50" />
            <SearchableFilterSelect value={projectManagerFilter} onChange={setProjectManagerFilter} options={projectManagerOptions} searchPlaceholder={labels.search} emptyLabel={t("common:noResults")} title={labels.projectManager} containerClassName="w-[166px] xl:w-[182px] min-w-[166px]" buttonClassName="border-amber-200 focus:ring-2 focus:ring-amber-500 focus:border-amber-400 font-semibold text-amber-700 hover:bg-amber-50" />

            <input type="date" value={projectStartDate} onChange={(event) => setProjectStartDate(event.target.value)} className="w-[100px] xl:w-[112px] px-1 py-1.5 text-[13px] border-2 border-pink-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-pink-500 focus:border-pink-400 shadow-sm font-semibold text-pink-700 transition-all hover:bg-pink-50 shrink-0" placeholder={labels.startDate} title={labels.startDate} />
            <input type="date" value={projectEndDate} onChange={(event) => setProjectEndDate(event.target.value)} className="w-[100px] xl:w-[112px] px-1 py-1.5 text-[13px] border-2 border-rose-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-rose-500 focus:border-rose-400 shadow-sm font-semibold text-rose-700 transition-all hover:bg-rose-50 shrink-0" placeholder={labels.endDate} title={labels.endDate} />

            {(projectWorkflowFilter !== "all" ||
              projectArchiveFilter !== "all" ||
              projectClientFilter !== "all" ||
              projectManagerFilter !== "all" ||
              projectStartDate ||
              projectEndDate) && (
              <button
                onClick={clearProjectFilters}
                className="flex items-center gap-1 px-1.5 py-1.5 text-[13px] border-2 border-red-200 rounded-lg bg-white/80 backdrop-blur-sm focus:ring-2 focus:ring-red-500 font-semibold text-red-600 transition-all hover:bg-red-50 shadow-sm whitespace-nowrap shrink-0"
              >
                <XMarkIcon className="w-4 h-4" />
                <span className="hidden xl:inline">{t("common:clear")}</span>
              </button>
            )}

            <div className="ml-auto flex items-center gap-1.5 shrink-0">
              <div className="flex items-center gap-1.5 px-2 py-1.5 bg-white/60 backdrop-blur-sm rounded-lg border-2 border-purple-200 shadow-sm shrink-0">
                <span className="text-[13px] font-bold text-purple-700 leading-none">
                  {projectItemsWithMetrics.length}
                </span>
                <span className="hidden xl:inline text-[13px] text-purple-600">
                  {labels.projects}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div ref={tableCardRef} className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-sm border border-white/20 overflow-hidden flex flex-col" style={tableCardStyle}>
        <div ref={scrollContainerRef} onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)} className="relative overflow-auto min-h-0 flex-1">
          {loading && (
            <div className="pointer-events-none absolute inset-x-0 top-28 z-50 flex justify-center px-4">
              <div className="inline-flex items-center justify-center gap-3 rounded-full border border-purple-200 bg-white/95 px-4 py-2 text-sm font-medium text-purple-700 shadow-sm backdrop-blur-sm">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-purple-300 border-t-purple-700" />
                {labels.loadingData}
              </div>
            </div>
          )}
          {!loading && projects.length === 0 ? (
            <div className="text-center py-16">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl mb-6">
                <FolderIcon className="w-10 h-10 text-purple-600" />
              </div>
              <p className="text-gray-900 text-sm font-semibold mb-2">{labels.noProjects}</p>
              <p className="text-gray-500 text-sm">{labels.noProjectsHint}</p>
            </div>
          ) : (
            <div className="relative">
              <ActivityTableConfigurator
                title={labels.tableConfiguratorTitle}
                description={t(
                  "dashboard:managerProjects.projectStartFieldsDescription",
                  {
                    defaultValue:
                      "Projeler sekmesinde görünecek sabit kolonları ve proje başlangıç alanlarını seçin.",
                  },
                )}
                isOpen={isProjectTableConfiguratorOpen}
                selectedColumns={projectVisibleColumns}
                defaultColumns={projectDefaultColumns}
                formColumns={projectAvailableFormColumns}
                selectedColumnsTitle={labels.selectedColumnsTitle}
                defaultColumnsTitle={labels.defaultColumnsTitle}
                formColumnsTitle={labels.projectStartFieldsTitle}
                defaultColumnKindLabel={labels.defaultColumnLabel}
                formColumnKindLabel={labels.projectFieldColumnLabel}
                saving={isSavingProjectTableLayout}
                onClose={() => setIsProjectTableConfiguratorOpen(false)}
                onSave={persistProjectTableLayout}
              />
              <div className="space-y-3 p-3 md:hidden">
                {loading ? (
                  <div className="flex items-center justify-center gap-3 py-8 text-sm text-purple-700">
                    <div className="h-5 w-5 animate-spin rounded-full border-2 border-purple-300 border-t-purple-700" />
                    {labels.loadingData}
                  </div>
                ) : projectItemsWithMetrics.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-gray-200 bg-white p-6 text-center text-sm text-gray-500">
                    {labels.noProjectsForFilter}
                  </div>
                ) : (
                  visiblePaginatedProjectItems.map((item: any) => {
                    const previewColumns = projectVisibleColumns
                      .filter((column) => column.id !== "projectName")
                      .slice(0, 4);
                    const hiddenColumnCount = Math.max(
                      projectVisibleColumns.length - 1 - previewColumns.length,
                      0
                    );

                    return (
                      <div
                        key={`mobile-project-${item.project.id}`}
                        className="rounded-lg border border-purple-100 bg-white px-3 py-2 shadow-sm"
                      >
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 flex h-7 min-w-7 items-center justify-center rounded-md bg-purple-50 text-[11px] font-bold text-purple-700">
                            {String(item.projectName || "-").slice(0, 2)}
                          </span>
                          <button
                            type="button"
                            onClick={() =>
                              navigate(`/project-instances/${item.project.id}`, {
                                state: {
                                  backTo: "/manager-dashboard",
                                  backState: { mainTab: "projects" },
                                },
                              })
                            }
                            className="min-w-0 flex-1 text-left"
                          >
                            <div className="flex min-w-0 items-center gap-2">
                              <p className="truncate text-[13px] font-bold text-gray-900">
                                {item.projectName || "-"}
                              </p>
                              <span className="shrink-0 rounded-full bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-700">
                                {item.projectCode || "-"}
                              </span>
                            </div>
                            <p className="mt-0.5 truncate text-[11px] font-medium text-gray-500">
                              {item.project.templateWorkflowName || "-"} -{" "}
                              {item.clientName || "-"}
                            </p>
                            <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-[11px] font-semibold text-slate-600">
                              <span>{labels.projectManager}: {item.managerName || "-"}</span>
                              <span>{labels.spentHours}: {formatHoursTr(item.spentHours)}</span>
                              {item.metrics?.hasPlannedHours && (
                                <span>
                                  {labels.plannedHours}:{" "}
                                  {formatHoursTr(item.metrics.plannedHours)}
                                </span>
                              )}
                            </div>
                          </button>
                          <div className="flex shrink-0 items-center gap-1">
                            <button
                              onClick={() => handleViewProjectFiles(item.project, item.formData)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-blue-50 text-blue-700"
                              title={labels.files}
                              aria-label={labels.files}
                            >
                              <FolderIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() =>
                                navigate(`/project-instances/${item.project.id}`, {
                                  state: {
                                    backTo: "/manager-dashboard",
                                    backState: { mainTab: "projects" },
                                  },
                                })
                              }
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-indigo-50 text-indigo-700"
                              title={t("common:details")}
                              aria-label={t("common:details")}
                            >
                              <EyeIcon className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => handleEditProject(item.project)}
                              className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-orange-50 text-orange-700"
                              title={t("common:edit")}
                              aria-label={t("common:edit")}
                            >
                              <PencilSquareIcon className="h-4 w-4" />
                            </button>
                          </div>
                        </div>

                        {previewColumns.length > 0 && (
                          <div className="mt-2 flex flex-wrap gap-1.5 border-t border-purple-50 pt-2">
                            {previewColumns.map((column) => (
                              <span
                                key={`mobile-project-${item.project.id}-${column.kind}-${column.id}`}
                                className="max-w-full truncate rounded-md bg-slate-50 px-2 py-1 text-[11px] font-semibold text-slate-700"
                              >
                                {column.label}: {renderProjectCell(item, column)}
                              </span>
                            ))}
                            {hiddenColumnCount > 0 && (
                              <span className="rounded-md bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600">
                                +{hiddenColumnCount} {labels.selected}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <table className="hidden min-w-full divide-y divide-gray-200 md:table">
                <thead className="bg-gradient-to-r from-purple-100 to-pink-100 sticky top-0 z-30 border-b border-gray-200">
                  <tr>
                    {projectVisibleColumns.map((column) => {
                      const sortField = getSortFieldForColumn(column);
                      const isSticky = column.id === "projectName";
                      const isNumeric = isNumericProjectColumn(column);
                      return (
                        <th key={`project-header-${column.kind}-${column.id}`} onClick={() => handleProjectSort(sortField)} className={`px-4 py-2 text-xs font-bold text-purple-800 uppercase tracking-wider cursor-pointer hover:bg-purple-200 transition-colors ${isNumeric ? "text-right" : "text-left"} ${isSticky ? "sticky top-0 left-0 z-40 bg-gradient-to-r from-purple-100 to-pink-100 border-r border-purple-200" : ""} ${DEFAULT_COLUMN_MIN_WIDTHS[column.id] || "min-w-[180px]"}`} title={labels.sort}>
                          <div className={`flex items-center gap-1 ${isNumeric ? "justify-end" : ""}`}>
                            <span className="leading-tight whitespace-nowrap">{column.label}</span>
                            <SortIcon field={sortField} currentField={projectSortField} currentOrder={projectSortOrder} />
                          </div>
                        </th>
                      );
                    })}
                  </tr>
                  <tr className="bg-purple-50/90 border-t border-purple-200">
                    {projectVisibleColumns.map((column) => (
                      <th key={`project-filter-${column.kind}-${column.id}`} className={`px-3 py-1.5 ${column.id === "projectName" ? "sticky left-0 z-40 bg-purple-50/95 border-r border-purple-200" : ""}`}>
                        {renderFilterCell(column)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {loading ? (
                    <tr><td colSpan={visibleColumnCount} className="px-4 py-10 text-center text-sm text-purple-700">&nbsp;</td></tr>
                  ) : projectItemsWithMetrics.length === 0 ? (
                    <tr><td colSpan={visibleColumnCount} className="px-4 py-8 text-center text-sm text-gray-500">{labels.noProjectsForFilter}</td></tr>
                  ) : null}
                  {!loading && virtualizationEnabled && topSpacerHeight > 0 && (
                    <tr aria-hidden="true"><td colSpan={visibleColumnCount} style={{ height: `${topSpacerHeight}px`, padding: 0 }} className="border-0 bg-transparent" /></tr>
                  )}
                  {!loading &&
                    visiblePaginatedProjectItems.map((item: any) => (
                      <tr key={item.project.id} className="group hover:bg-purple-50/40 transition-colors">
                        {projectVisibleColumns.map((column) => {
                          const metrics = item.metrics;
                          const isSticky = column.id === "projectName";
                          const isNumeric = isNumericProjectColumn(column);
                          const isRemainingHoursNegative =
                            column.id === "remainingHours" &&
                            metrics.remainingHoursRaw !== null &&
                            metrics.remainingHoursRaw < 0;
                          const textColorClass =
                            column.id === "spentHours" ? "text-orange-600 font-semibold" :
                            column.id === "plannedHours" ? "text-slate-700 font-semibold" :
                            column.id === "remainingHours" ? (isRemainingHoursNegative ? "text-rose-700 font-semibold" : "text-cyan-700 font-semibold") :
                            column.id === "projectAmount" ? "text-sky-700 font-semibold" :
                            column.id === "vatAmount" || column.id === "invoiceTotal" ? "text-slate-700 font-semibold" :
                            column.id === "totalCollected" ? "text-teal-700 font-semibold" :
                            column.id === "remainingCollection" ? "text-rose-700 font-semibold" :
                            column.id === "realizedCost" ? "text-emerald-700 font-semibold" :
                            column.id === "remainingEstimatedCost" ? "text-amber-700 font-semibold" :
                            column.id === "totalEstimatedCost" ? "text-indigo-700 font-semibold" :
                            column.id === "netProfit" ? "text-fuchsia-700 font-semibold" :
                            column.id === "estimatedNetProfit" ? "text-violet-700 font-semibold" :
                            "text-gray-700";

                          return (
                            <td key={`project-row-${item.project.id}-${column.kind}-${column.id}`} className={`px-4 py-1 text-xs whitespace-nowrap ${isNumeric ? "text-right" : "text-left"} ${textColorClass} ${isSticky ? "sticky left-0 z-20 bg-white border-r border-gray-200 group-hover:bg-purple-50 min-w-[400px] max-w-[460px] overflow-hidden shadow-[1px_0_0_rgba(229,231,235,1)]" : "relative z-0"}`}>
                              {renderProjectCell(item, column)}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  {!loading && virtualizationEnabled && bottomSpacerHeight > 0 && (
                    <tr aria-hidden="true"><td colSpan={visibleColumnCount} style={{ height: `${bottomSpacerHeight}px`, padding: 0 }} className="border-0 bg-transparent" /></tr>
                  )}
                  {!loading && projectItemsWithMetrics.length > paginatedProjectItems.length && (
                    <tr className="bg-purple-100/80 border-y-2 border-purple-200">
                      <td
                        colSpan={visibleColumnCount}
                        className="px-4 py-3 text-sm font-bold text-purple-800"
                      >
                        ... {projectItemsWithMetrics.length - paginatedProjectItems.length}{" "}
                        {t("dashboard:managerProjects.recordsMore", {
                          defaultValue: "kayıt daha",
                        })}
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="sticky bottom-0 z-20 bg-purple-50/95 backdrop-blur border-t-2 border-purple-200">
                  <tr>
                    {projectVisibleColumns.map((column) => {
                      const isSticky = column.id === "projectName";
                      const isNumeric = isNumericProjectColumn(column);
                      const isRemainingHoursNegative =
                        column.id === "remainingHours" &&
                        summaryStats.remainingHours !== null &&
                        summaryStats.remainingHours < 0;
                      const textColorClass =
                        column.id === "spentHours" ? "text-orange-700" :
                        column.id === "plannedHours" ? "text-slate-700" :
                        column.id === "remainingHours" ? (isRemainingHoursNegative ? "text-rose-700" : "text-cyan-700") :
                        column.id === "projectAmount" ? "text-sky-700" :
                        column.id === "vatAmount" || column.id === "invoiceTotal" ? "text-slate-700" :
                        column.id === "totalCollected" ? "text-teal-700" :
                        column.id === "remainingCollection" ? "text-rose-700" :
                        column.id === "realizedCost" ? "text-emerald-700" :
                        column.id === "remainingEstimatedCost" ? "text-amber-700" :
                        column.id === "totalEstimatedCost" ? "text-indigo-700" :
                        column.id === "netProfit" ? "text-fuchsia-700" :
                        column.id === "estimatedNetProfit" ? "text-violet-700" :
                        "text-purple-900";
                      return (
                        <td key={`project-summary-${column.kind}-${column.id}`} className={`px-4 py-2 text-xs font-bold ${isNumeric ? "text-right whitespace-nowrap" : "text-left"} ${textColorClass} ${isSticky ? "sticky left-0 z-30 bg-purple-50/95 border-r border-purple-200 min-w-[400px]" : ""}`}>
                          {renderSummaryCell(column)}
                        </td>
                      );
                    })}
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
        {projectItemsWithMetrics.length > 0 && (
          <div className="px-4 py-2 border-t border-gray-200 bg-gray-50">
            <div className="flex flex-col md:flex-row items-center justify-between gap-1.5">
              <div className="text-xs text-gray-600">
                <span className="font-medium">{projectItemsWithMetrics.length}</span> {labels.recordsFound}
                {combinedHasActiveFilters && <span className="text-gray-400"> ({labels.totalRecordsFrom} {projects.length})</span>}
              </div>
              <div className="flex items-center gap-1.5">
                {projectTotalPages > 1 && (
                  <>
                    <button onClick={() => setProjectCurrentPage(projectCurrentPage - 1)} disabled={projectCurrentPage === 1} className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronLeftIcon className="w-4 h-4" /></button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: projectTotalPages }, (_, index) => index + 1)
                        .filter((page) => page === 1 || page === projectTotalPages || Math.abs(page - projectCurrentPage) <= 1)
                        .map((page: number, index: number, items: number[]) => (
                          <React.Fragment key={page}>
                            {index > 0 && items[index - 1] !== page - 1 && <span className="px-2 text-gray-400">...</span>}
                            <button onClick={() => setProjectCurrentPage(page)} className={`px-2.5 py-1.5 text-xs rounded-md ${projectCurrentPage === page ? "bg-purple-600 text-white font-medium" : "border border-gray-300 hover:bg-gray-100"}`}>{page}</button>
                          </React.Fragment>
                        ))}
                    </div>
                    <button onClick={() => setProjectCurrentPage(projectCurrentPage + 1)} disabled={projectCurrentPage === projectTotalPages} className="px-2.5 py-1.5 text-xs border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"><ChevronRightIcon className="w-4 h-4" /></button>
                  </>
                )}
                <div className={`${projectTotalPages > 1 ? "ml-2" : ""} flex items-center gap-1.5`}>
                  <button type="button" onClick={() => setIsProjectTableConfiguratorOpen((prev) => !prev)} className="inline-flex items-center h-7 px-2.5 py-1 text-xs font-bold rounded-md shadow-md border border-purple-200 bg-white text-purple-800 transition whitespace-nowrap shrink-0 hover:bg-purple-50" title={labels.tableEdit}>
                    <Cog6ToothIcon className="w-4 h-4 mr-1.5" />
                    <span className="hidden xl:inline">{labels.tableEdit}</span>
                    <span className="xl:hidden">{labels.shortEdit}</span>
                  </button>
                  <button type="button" onClick={exportProjectsToExcel} disabled={projectItemsWithMetrics.length === 0} className={`inline-flex items-center h-7 px-2.5 py-1 text-xs font-bold rounded-md shadow-md border border-transparent text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition whitespace-nowrap shrink-0 ${projectItemsWithMetrics.length === 0 ? "opacity-50 cursor-not-allowed" : "hover:shadow-lg"}`} title={labels.downloadExcel}>
                    <DocumentTextIcon className="w-4 h-4 mr-1.5" />
                    <span className="hidden xl:inline">{labels.downloadExcel}</span>
                    <span className="xl:hidden">Excel</span>
                  </button>
                  <select value={projectItemsPerPage} onChange={(event) => { setProjectItemsPerPage(Number(event.target.value)); setProjectCurrentPage(1); }} className="h-7 px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500">
                    <option value={200}>200 {t("common:itemsPerPage")}</option>
                    <option value={500}>500 {t("common:itemsPerPage")}</option>
                    <option value={1000}>1000 {t("common:itemsPerPage")}</option>
                    <option value={-1}>{labels.showAll}</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
